import Link from "next/link"
import { PreviewBusiness } from "@/schema"
import { ButtonBadge } from "@ui/button-badge"
import { Badge } from "@ui/shad"
import { Building2, ExternalLink, Globe, Tag } from "lucide-react"

import { usePeekViewData } from "@/hooks/api/peek-views/queries"
import { Link as BookLink } from "@/components/book-link"
import { PhoneBadgeWithRingout } from "@/components/tables/phone-badge-with-ringout"

import PeekViewSkeleton from "./skeleton"

export default function BusinessPeekView({ businessId, href }: { businessId: string; href: string | null }) {
  const { data: business, isLoading } = usePeekViewData(businessId, "Business") as {
    data: PreviewBusiness
    isLoading: boolean
  }

  if (isLoading) return <PeekViewSkeleton />

  return (
    <div className="flex w-xs flex-col items-stretch gap-2 xl:w-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-baseline">
          <p className="truncate font-medium">{business.name}</p>
          {href && (
            <BookLink
              href={href}
              className="text-muted-foreground hover:text-link ml-2 transition-colors"
              onClick={(e) => e.stopPropagation()}
              aria-label="Open in new page"
            >
              <ExternalLink className="size-3.5" strokeWidth={1.5} />
            </BookLink>
          )}
        </div>
        <Badge variant="outline" className="flex-none">
          Business
        </Badge>
      </div>
      <div className="flex items-center gap-2">
        <Building2 className="text-icon size-4 flex-none" />
        {business.email && (
          <ButtonBadge text={business.email || ""} copyText={business.email || ""} className="max-w-1/2 truncate" />
        )}
        {business.phone_number && (
          <PhoneBadgeWithRingout
            value={business.phone_number}
            extension={business.phone_number_extension}
            name={business.name}
          />
        )}
      </div>
      {business.website_url && (
        <div className="flex items-center gap-2">
          <Globe className="text-icon size-4 flex-none" />
          <Link
            href={business.website_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-link hover:text-link-hover border-link/40 hover:border-link-hover max-w-1/2 truncate border-b border-dashed text-xs"
            onClick={(e) => e.stopPropagation()}
          >
            {business.website_url}
          </Link>
        </div>
      )}
      {business.industry && (
        <div className="flex items-center gap-2">
          <Building2 className="text-icon size-4 flex-none" />
          <span className="text-tertiary-foreground text-xs">{business.industry}</span>
        </div>
      )}
      <div className="flex items-center gap-2 truncate">
        <Tag className="text-icon size-4 flex-none" />

        {business.tags.slice(0, 3).map((tag) => (
          <Badge key={tag.id} variant="secondary" className="flex-none">
            {tag.name}
          </Badge>
        ))}
        {business.tags.length > 3 && (
          <Badge variant="secondary" className="flex-none">
            +{business.tags.length - 3}
          </Badge>
        )}
      </div>
    </div>
  )
}
